import { WorkOrder } from '../job/workorder';
import { stageIsOpenForContractor } from '../../security/capabilities/job/stage/stage.claim.state';
import { Task } from './task';
import { TaskTarget } from './task.enum';
import { isTaskAttachmentField, TASK_EVIDENCE_RESPONSE_FIELDS } from './task.evidence';
import { sameAsStoredValue } from '../../utilities/stored.value.equality';

/** The stage a task hangs off and the item tasks the contractor may not see, resolved by the host before the rules run. */
export interface ContractorTaskScope {
    stage: WorkOrder | null | undefined;
    /** Ids of item tasks (and their wrappers) for lines the subcontractor does not supply. */
    hiddenItemTaskIds: ReadonlySet<string>;
}

export const CONTRACTOR_TASK_STAGE_CLOSED = 'This stage is not open for claim changes.';
export const CONTRACTOR_TASK_NOT_VISIBLE = 'This task is not available to your company.';
export const CONTRACTOR_TASK_FIELD_LOCKED = 'You can change only the status and the evidence of this task.';
export const CONTRACTOR_TASK_NOT_ATTACHMENT_FIELD = 'Attachments can be added only to the evidence of this task.';

/** The evidence a field patch may change; the status moves only through the validated status change, and every other field is the office's (WBS-12027). */
const CONTRACTOR_TASK_ALLOWED_FIELDS: ReadonlySet<string> = new Set(['_id', 'answered', 'additionalFiles', ...TASK_EVIDENCE_RESPONSE_FIELDS]);

/**
 * Whether a subcontractor sees the task at all: a Field task on the stage that is not the task of a
 * line they do not supply. Same rule the legacy portal list applied in memory, so both tabs agree.
 */
export function isTaskVisibleToContractor(task: Pick<Task, '_id' | 'target' | 'active' | 'parent' | 'links'> | null | undefined, hiddenItemTaskIds: ReadonlySet<string>): boolean {

    if (!task || task.active === false || task.target !== TaskTarget.Field) return false;

    return !hiddenItemTaskIds.has(task._id) && !hiddenItemTaskIds.has(task.parent?._id) && !(task.links ?? []).some(link => hiddenItemTaskIds.has(link._id));
}

/** Why the contractor may not capture evidence on the task, or nothing when they may. */
export function resolveContractorTaskEvidenceRefusal(scope: ContractorTaskScope, task: Task | null | undefined): string | undefined {

    if (!stageIsOpenForContractor(scope.stage)) return CONTRACTOR_TASK_STAGE_CLOSED;
    if (!isTaskVisibleToContractor(task, scope.hiddenItemTaskIds)) return CONTRACTOR_TASK_NOT_VISIBLE;

    return undefined;
}

/** `resolveContractorTaskEvidenceRefusal` for an attachment add or remove, whose target field the caller names. */
export function resolveContractorTaskAttachmentRefusal(scope: ContractorTaskScope, task: Task | null | undefined, field: string | null | undefined): string | undefined {

    return resolveContractorTaskEvidenceRefusal(scope, task) ?? (isTaskAttachmentField(field) ? undefined : CONTRACTOR_TASK_NOT_ATTACHMENT_FIELD);
}

/** Why the contractor may not apply a field patch, or nothing when they may. A status, its stamp or its completion metadata that differs from the stored one is refused like any other locked field. */
export function resolveContractorTaskWriteRefusal(scope: ContractorTaskScope, task: Task | null | undefined, patch: Partial<Task>): string | undefined {

    return resolveContractorTaskEvidenceRefusal(scope, task) ?? (changesLockedField(task, patch) ? CONTRACTOR_TASK_FIELD_LOCKED : undefined);
}

// Compared by value, not by key: a caller that sends the whole task back must not be refused for the fields it left alone.
function changesLockedField(task: Task | null | undefined, patch: Partial<Task>): boolean {

    const stored: Partial<Record<string, unknown>> = { ...task };

    return Object.entries(patch ?? {}).some(([field, value]) => !CONTRACTOR_TASK_ALLOWED_FIELDS.has(field) && !sameAsStoredValue(stored[field], value));
}
